import { EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Card, Tag } from 'antd';
import { parse, formatDate } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz/formatInTimeZone';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router';

import { type IAdmissionSurvey, useUpdateAdmissionSurveyMutation } from '@/api/endpoints/schools/schoolSelectionPanel';
import { DataSection, DatePickerField, InputField, SwitchField } from '@/shared/components';
import { DATE_FORMATTERS, ROUTES, TIME_ZONE } from '@/shared/consts';
import { useApiRequest } from '@/shared/hooks';

import { ESelectionPanelTabs } from '../../../_components/SelectionPanelHeader/consts';
import { SelectionPanelHeader } from '../../../_components/SelectionPanelHeader/SelectionPanelHeader';
import { QuestionsList } from '../../_components/QuestionsList/QuestionsList';

import { UPDATE_SURVEY_VALIDATION_SCHEMA } from './consts';
import styles from './SurveyEditPage.module.scss';
import type { IFormData } from './types';

interface IProp {
  surveyData: IAdmissionSurvey;
}

export const SurveyEditPage = ({
  surveyData: { title: surveyName, start_date: surveyStartDate, end_date: surveyEndDate, active: isSurveyActive },
}: IProp) => {
  const { id: schoolId } = useParams();

  const [updateSurvey] = useUpdateAdmissionSurveyMutation();

  const methods = useForm<IFormData>({
    defaultValues: {
      surveyName,
      startDate: parse(surveyStartDate, DATE_FORMATTERS.FULL_YEAR_MONTH_DAY, new Date()),
      endDate: parse(surveyEndDate, DATE_FORMATTERS.FULL_YEAR_MONTH_DAY, new Date()),
      isActive: isSurveyActive,
    },
    resolver: yupResolver(UPDATE_SURVEY_VALIDATION_SCHEMA),
  });

  const {
    handleSubmit,
    watch,
    trigger,
    formState: { isValidating },
  } = methods;

  const sendApiRequest = useApiRequest();

  const navigate = useNavigate();

  const isActive = watch('isActive');
  const currentStartDate = watch('startDate');
  watch((_, { name }) => {
    if (name === 'startDate' && isValidating) {
      trigger('endDate');
    }
  });

  const updateSurveyHandle = handleSubmit(({ surveyName, startDate, endDate, isActive }) => {
    if (endDate) {
      sendApiRequest({
        apiRequestCallback: () =>
          updateSurvey({
            schoolId: Number(schoolId),
            surveyData: {
              title: surveyName,
              start_date: formatDate(startDate, DATE_FORMATTERS.FULL_YEAR_MONTH_DAY),
              end_date: formatDate(endDate, DATE_FORMATTERS.FULL_YEAR_MONTH_DAY),
              active: isActive,
            },
          }),
        successMessage: 'Опрос успешно обновлён',
        successCallback: () => {
          navigate(
            `/${ROUTES.PUBLIC_PAGES}/${ROUTES.SCHOOLS}/${schoolId}/${ROUTES.SELECTION_PANEL}/${ROUTES.ADMISSION_SURVEY}/${ROUTES.VIEW}`,
          );
        },
      });
    }
  });

  return (
    <div className={styles.wrapper}>
      <SelectionPanelHeader currentTab={ESelectionPanelTabs.AdmissionSurvey} />
      <div className={styles['header-wrapper']}>
        <div className={styles['page-info-wrapper']}>
          <p className={styles.title}>Редактирование опроса</p>
        </div>
        <div className={styles['header-action-wrapper']}>
          <Tag
            className={styles['status-tag']}
            icon={isActive ? <EyeOutlined /> : <EyeInvisibleOutlined />}
            color={isActive ? 'green' : 'default'}
          >
            {isActive ? 'Активен' : 'Не активен'}
          </Tag>
        </div>
      </div>

      <FormProvider {...methods}>
        <Card>
          <div className={styles['main-info-card-wrapper']}>
            <p className={styles['card-title']}>Основная информация</p>
            <DataSection
              titleText="Название опроса"
              isTitleRequired
            >
              <InputField
                fieldName="surveyName"
                placeholder="Например: Заявка на обучение 2026"
              />
            </DataSection>
            <div className={styles['survey-switcher-wrapper']}>
              <div className={styles['survey-switcher-info-wrapper']}>
                <p className={styles['survey-switcher-title']}>Опрос активен</p>
                <p className={styles['survey-switcher-description']}>Разрешить прием заявок через этот опрос</p>
              </div>
              <SwitchField fieldName="isActive" />
            </div>
            <div className={styles['half-width-sections-wrapper']}>
              <DataSection
                titleText="Дата начала"
                isTitleRequired
                wrapperClassName={styles['half-width-section']}
              >
                <DatePickerField
                  fieldName="startDate"
                  format={DATE_FORMATTERS.DAY_MONTH_FULL_YEAR}
                  placement="topRight"
                  minDate={formatInTimeZone(new Date(), TIME_ZONE.MOSCOW, DATE_FORMATTERS.FULL_YEAR_MONTH_DAY)}
                  disabled={
                    formatInTimeZone(new Date(), TIME_ZONE.MOSCOW, DATE_FORMATTERS.FULL_YEAR_MONTH_DAY) >=
                    surveyStartDate
                  }
                />
              </DataSection>
              <DataSection
                titleText="Дата окончания"
                isTitleRequired
                wrapperClassName={styles['half-width-section']}
              >
                <DatePickerField
                  fieldName="endDate"
                  format={DATE_FORMATTERS.DAY_MONTH_FULL_YEAR}
                  placement="topRight"
                  minDate={
                    formatInTimeZone(new Date(), TIME_ZONE.MOSCOW, DATE_FORMATTERS.FULL_YEAR_MONTH_DAY) >=
                    formatInTimeZone(surveyStartDate, TIME_ZONE.MOSCOW, DATE_FORMATTERS.FULL_YEAR_MONTH_DAY)
                      ? surveyEndDate
                      : currentStartDate
                  }
                  disabled={
                    formatInTimeZone(new Date(), TIME_ZONE.MOSCOW, DATE_FORMATTERS.FULL_YEAR_MONTH_DAY) > surveyEndDate
                  }
                />
              </DataSection>
            </div>
          </div>
        </Card>
      </FormProvider>
      <Card>
        <div className={styles['main-info-card-wrapper']}>
          <p className={styles['card-title']}>Поля опроса</p>
          <QuestionsList isEditMode />
        </div>
      </Card>
      <div className={styles['buttons-wrapper']}>
        <Button
          onClick={() => {
            navigate(
              `/${ROUTES.PUBLIC_PAGES}/${ROUTES.SCHOOLS}/${schoolId}/${ROUTES.SELECTION_PANEL}/${ROUTES.ADMISSION_SURVEY}/${ROUTES.VIEW}`,
            );
            window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
          }}
        >
          Отмена
        </Button>
        <Button
          type="primary"
          onClick={updateSurveyHandle}
        >
          Сохранить изменения
        </Button>
      </div>
    </div>
  );
};
